import { combineReducers } from 'redux';
import { normalizeLicenseType } from '../../settings/pricing-options';
import { columnsByName } from './constants';

const updateList = (id, list, state) => {
  return {
    ...state,
    items: {
      ...state.items,
      [id]: list,
    },
  };
};

const updateListValues = (listId, changes, state) => {
  const list = state.items[listId];
  if (!list) return state;
  const newList = {
    ...list,
    ...changes,
  };
  return updateList(listId, newList, state);
};

const addItem = (listId, item, otherChanges, state) => {
  const list = state.items[listId];
  if (!list) return state;
  const newList = {
    ...list,
    ...otherChanges,
  };
  const newIds = [];
  if (!newList.ids.includes(item.id)) newIds.push(item.id);
  newList.ids = newIds.concat(list.ids);
  newList.newIds = newIds;
  newList.data[item.id] = item;
  return updateList(listId, newList, state);
};

const removeItem = (listId, itemId, otherChanges, state) => {
  const list = state.items[listId];
  if (!list) return state;
  const newList = {
    ...list,
    ...otherChanges,
  };
  newList.ids = newList.ids.filter(id => id !== itemId);
  delete newList.data[itemId];
  return updateList(listId, newList, state);
};

const filterBySearch = (itemFilterCallback, listId, term, state) => {
  const list = state.items[listId];
  if (!list) return state;
  const newList = {
    ...list,
    searchFilterString: term,
  };
  if (term === '') {
    newList.filteredIds = null;
  } else {
    const filteredIds = newList.ids;
    newList.filteredIds = filteredIds.filter(id => {
      const item = list.data[id];
      return itemFilterCallback(item, term);
    });
  }
  return updateList(listId, newList, state);
};

const filterList = (filterCallback, filterName, listId, state) => {
  const list = state.items[listId];
  if (!list) return state;
  const newList = { ...list, searchFilterString: null };
  newList.activeFilter =
    filterName === newList.activeFilter ? null : filterName;
  newList.filteredIds = newList.ids.filter(id => {
    const item = list.data[id];
    return filterCallback(item);
  });
  return updateList(listId, newList, state);
};

const sortListIds = list => {
  const items = Object.keys(list.data).map(id => ({ id, row: list.data[id] }));
  const createVal = item => {
    // I really wish our user objects were flat. Sigh.
    let val =
      list.sortColumn === 'username'
        ? item.row.auth_info.username
        : item.row[list.sortColumn];
    // Unfortunately, we have some null values from the API
    if (val === null) val = '';
    // We need to normalize our licenses before sorting.
    if (list.sortColumn === 'license_type') val = normalizeLicenseType(val);
    return val;
  };

  items.sort((a, b) => {
    const valA = createVal(a);
    const valB = createVal(b);
    if (typeof valA === 'string') return valA.localeCompare(valB);
    return valB - valA;
  });

  const sortedIds = items.map(item => item.id);
  const newList = {
    ...list,
    ids: list.sortAscending ? sortedIds : sortedIds.reverse(),
  };
  if (list.filteredIds) {
    const filteredIds = items.reduce((sortedFilterIds, item) => {
      if (list.filteredIds.includes(item.id)) sortedFilterIds.push(item.id);
      return sortedFilterIds;
    }, []);
    newList.filteredIds = list.sortAscending
      ? filteredIds
      : filteredIds.reverse();
  }
  return newList;
};

const sortByColumn = (listId, sortColumn, state) => {
  const list = state.items[listId];
  if (!list) return state;
  if (list.sortColumn === sortColumn) {
    return updateList(
      listId,
      {
        ...list,
        ids: list.ids.reverse(),
        filteredIds: list.filteredIds ? list.filteredIds.reverse() : null,
        sortAscending: !list.sortAscending,
      },
      state
    );
  }
  const updatedList = {
    ...list,
    sortAscending: true,
    sortColumn,
  };
  return updateList(listId, sortListIds(updatedList), state);
};

const updateStatsList = (resp, type, state) => {
  const listType = `${type}_stats`;
  const list = state.items[listType];
  if (!list) return state;
  const ids = [];
  const data = {};
  resp.forEach(item => {
    const id = item.user_id || item.school_id;
    ids.push(id);
    data[id] = item;
  });
  const updatedList = {
    ...list,
    ids,
    data,
    isFetching: false,
  };
  return updateList(listType, sortListIds(updatedList), state);
};

const updateUsersListContents = (listId, resp, state) => {
  const list = state.items[listId];
  if (!list) return state;
  const items = resp.entities[list.itemType] || {};
  const updatedList = {
    ...list,
    data: items,
    ids: resp.result,
    isFetching: false,
  };
  return updateList(listId, sortListIds(updatedList), state);
};

const listGenerator = name => {
  const basics = {
    name,
    isFetching: true,
    columnIds: columnsByName(name),
    data: {},
    ids: [],
    filteredIds: null,
    activeFilter: null,
    newIds: [],
    searchFilterString: null,
    message: null,
    sortAscending: true,
  };
  if (name === 'educators_stats' || name === 'schools_stats')
    return {
      ...basics,
      itemType: 'users',
      sortColumn: 'full_name',
    };
  return {
    ...basics,
    itemType: 'users',
    sortColumn: 'family_name',
  };
};

const byId = (
  state = {
    items: {},
  },
  action
) => {
  switch (action.type) {
    case 'INITIALIZE_LIST':
      return {
        ...state,
        items: {
          ...state.items,
          [action.id]: listGenerator(action.name),
        },
      };
    case 'FETCH_USER_FAILURE':
      return updateListValues(action.name, { isFetching: false }, state);
    case 'FETCH_USER_GROUP_SUCCESS':
      return updateUsersListContents(action.name, action.response, state);
    case 'FETCH_STUDENTS_IN_CLASS_SUCCESS':
      return updateUsersListContents(
        `students_in_class_${action.classId}`,
        action.response,
        state
      );
    case 'FETCH_STATS_SUCCESS':
      return updateStatsList(action.response.results, action.statsType, state);
    case 'ADD_USER_TO_USER_GROUP': {
      const addUserChanges = {};
      if (action.message) addUserChanges.message = action.message;
      return addItem(
        action.name,
        action.response.entities.users[action.response.result[0]],
        {},
        state
      );
    }
    case 'REMOVE_USER_FROM_USER_GROUP': {
      const removeUserChanges = {};
      if (action.message) removeUserChanges.message = action.message;
      return removeItem(action.name, action.id, removeUserChanges, state);
    }
    case 'REMOVE_STUDENT_FROM_CLASS_SUCCESS':
      return removeItem(
        `students_in_class_${action.classId}`,
        action.studentId,
        {
          message: {
            type: 'success',
            msgId: 'classes_page.remove_student.success_message',
          },
        },
        state
      );
    case 'ADD_STUDENT_TO_CLASS':
      return addItem(
        `students_in_class_${action.classId}`,
        action.response.entities.users[action.response.result[0]],
        {},
        state
      );
    case 'CLEAR_USER_GROUP_MESSAGE':
      return updateListValues(action.id, { message: null }, state);
    case 'UPDATE_USER_GROUP_MESSAGE':
      return updateListValues(action.id, { message: action.message }, state);
    case 'CLEAR_NEW_USER_IDS_FROM_LIST':
      return updateListValues(action.id, { newIds: [] }, state);
    case 'UPDATE_LIST_SORT_COLUMN':
      return sortByColumn(action.id, action.key, state);
    case 'UPDATE_LIST_SEARCH_FILTER':
      return filterBySearch(
        action.itemFilterCallback,
        action.id,
        action.value.toLowerCase(),
        state
      );
    case 'UPDATE_LIST_FILTER': {
      return filterList(action.callback, action.filter, action.id, state);
    }
    case 'CLEAR_LIST_FILTER':
    case 'CLEAR_LIST_SEARCH_FILTER':
      return updateListValues(
        action.id,
        { filteredIds: null, activeFilter: null, searchFilterString: null },
        state
      );
    case 'CLEAR_ALL_LIST_MESSAGES': {
      const clearedMessagesItems = {};
      Object.keys(state.items).forEach(id => {
        clearedMessagesItems[id] = {
          ...state.items[id],
          message: null,
        };
      });
      return {
        items: clearedMessagesItems,
      };
    }
    case 'POST_LOGOUT_SUCCESS':
    case 'CHANGE_ACTIVE_SCHOOL':
      return {
        items: {},
      };
    default:
      return state;
  }
};

const lists = combineReducers({
  byId,
});

export default lists;

export const getListById = (state, id) => {
  const list = state.byId.items[id];
  if (!list) return null;
  return {
    ...list,
    listId: id,
    count: list.ids.length,
  };
};

export const getClassListById = (state, classId) =>
  getListById(state, `students_in_class_${classId}`);
export const getNewIdsForList = (state, id) => {
  const list = state.byId.items[id];
  if (!list) return null;
  return list.newIds;
};
